import Button from '@components/V4/Button';
import { PaginationProps } from '@components/V4/Pagination';
import Table, { SortOrder } from '@components/V4/Table/Table';
import Tooltip from '@components/V4/Tooltip';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { SearchEnrollmentsDTO } from '@shared/clientTypes/DTOs/EnrollmentsDTO';
import { ENROLLMENT_STATUS_TYPES } from '@shared/constants';
import { getCourseProgressPercentage, mergeClassNames } from 'apps/agora/src/utils/helpers';
import { COLORS } from 'apps/agora/src/v3/global';
import moment from 'moment';
import { ChangeEvent, useEffect, useState } from 'react';

interface EnrollmentsTableProps {
  enrollments?: SearchEnrollmentsDTO['enrollments'];
  areEnrollmentsLoading: boolean;
  pagination: PaginationProps;
  sortBy: string | null;
  sortOrder: SortOrder | null;
  setSearchParams: (data: Partial<any>) => void;
  mentorModalOpenHandler: (enrollmentId: string) => void;
  enrollmentsModalOpenHandler: () => void;
  enrollmentModalOpenHandler: (enrollmentId: string) => void;
  openStudentDetailsModalHandler: (enrollmentId: string) => void;
  setSelectedEnrollments: React.Dispatch<React.SetStateAction<string[]>>;
  selectedEnrollments: string[];
}

const EnrollmentsTable = (props: EnrollmentsTableProps) => {
  const {
    enrollments,
    areEnrollmentsLoading,
    setSearchParams,
    pagination,
    sortBy,
    sortOrder,
    mentorModalOpenHandler,
    enrollmentModalOpenHandler,
    openStudentDetailsModalHandler,
    enrollmentsModalOpenHandler,
    setSelectedEnrollments,
    selectedEnrollments,
  } = props;

  const [isAllPageSelected, setIsAllPageSelected] = useState(false);

  const sortHandler = (field: string, order: SortOrder) => {
    setSearchParams({
      sortBy: order ? field : null,
      sortOrder: order || null,
      page: 1,
    });
  };

  const addSelectedEnrollment = (enrollmentId: string) => {
    setSelectedEnrollments((prev) => [...prev, enrollmentId]);
  };

  const removeSelectedEnrollment = (enrollmentId: string) => {
    setSelectedEnrollments((prev) => prev.filter((id) => id !== enrollmentId));
  };

  const toggleSelectedEnrollment = (enrollmentId: string) => {
    if (selectedEnrollments.includes(enrollmentId)) {
      removeSelectedEnrollment(enrollmentId);
    } else {
      addSelectedEnrollment(enrollmentId);
    }
  };

  const selectAllFromPageHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const currentPageIds = enrollments?.map((enrollment) => enrollment._id) || [];

    if (e.target.checked) {
      setSelectedEnrollments((prev) => [
        ...prev,
        ...currentPageIds.filter((id) => !prev.includes(id)),
      ]);
    } else {
      setSelectedEnrollments((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    }
  };

  useEffect(() => {
    const currentPageIds = enrollments?.map((enrollment) => enrollment._id) || [];

    const allSelected = currentPageIds.every((id) => selectedEnrollments.includes(id));
    setIsAllPageSelected(allSelected);
  }, [enrollments, selectedEnrollments]);

  return areEnrollmentsLoading ? (
    <div className="h-24 flex items-center justify-center text-customGrey">Loading...</div>
  ) : (
    <section className="relative px-18 mb-20">
      {selectedEnrollments.length > 0 && (
        <div className="flex gap-1 absolute -top-4 left-[4.5rem]">
          <Button onClick={enrollmentsModalOpenHandler} buttonText={'Bulk Edit'} />
          <span className="text-xs text-customGrey leading-6">
            {selectedEnrollments.length} enrollments selected
          </span>
        </div>
      )}
      <Table pagination={pagination} className="mt-1">
        <Table.Header>
          <Table.Row isHeader>
            <Table.HeaderCell>
              <input
                onChange={selectAllFromPageHandler}
                checked={isAllPageSelected}
                id="flags"
                aria-describedby="comments-description"
                name="flags"
                type="checkbox"
                className={mergeClassNames(
                  'h-4 w-4 mr-4 rounded border-customGrey text-customPrimary focus:ring-customPrimary bg-transparent cursor-pointer'
                )}
              />
              Course
            </Table.HeaderCell>
            <Table.HeaderCell>Student</Table.HeaderCell>
            <Table.HeaderCell>Mentor</Table.HeaderCell>
            <Table.HeaderCell
              onSort={(order) => sortHandler('startDate', order)}
              sortOrder={sortBy === 'startDate' ? sortOrder : undefined}
            >
              Start Date
            </Table.HeaderCell>
            <Table.HeaderCell
              onSort={(order) => sortHandler('endDate', order)}
              sortOrder={sortBy === 'endDate' ? sortOrder : undefined}
            >
              End Date
            </Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Last Session Date</Table.HeaderCell>
            <Table.HeaderCell
              onSort={(order) => sortHandler('completedSessions', order)}
              sortOrder={sortBy === 'completedSessions' ? sortOrder : undefined}
            >
              Completion
            </Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body hasResults={!areEnrollmentsLoading && !!enrollments?.length}>
          {enrollments?.map((enrollment) => (
            <Table.Row key={enrollment._id}>
              <Table.Cell>
                <input
                  onChange={() => toggleSelectedEnrollment(enrollment._id)}
                  checked={selectedEnrollments.includes(enrollment._id)}
                  value={enrollment._id}
                  id="flags"
                  aria-describedby="comments-description"
                  name="flags"
                  type="checkbox"
                  className={mergeClassNames(
                    'h-4 w-4 mr-4 rounded border-customGrey text-customPrimary focus:ring-customPrimary bg-transparent cursor-pointer'
                  )}
                />
                <span
                  className="underline cursor-pointer"
                  onClick={() => enrollmentModalOpenHandler(enrollment._id)}
                >
                  {enrollment.course?.title}
                </span>
              </Table.Cell>
              <Table.Cell
                onClick={
                  enrollment.student?._id
                    ? () => openStudentDetailsModalHandler(enrollment.student?._id)
                    : undefined
                }
              >
                <Tooltip text={enrollment.student?.isDeactivated ? 'Deactivated' : undefined}>
                  <span
                    className={mergeClassNames('flex gap-1', {
                      'line-through': enrollment.student?.isDeactivated,
                    })}
                  >
                    {enrollment.student?.name ?? 'n/a'}{' '}
                    {enrollment.student?.isDeactivated && (
                      <ExclamationCircleIcon className="w-4 h-4" aria-label="Deactivated user" />
                    )}
                  </span>
                </Tooltip>
              </Table.Cell>
              <Table.Cell
                onClick={
                  enrollment.mentor?._id
                    ? () => mentorModalOpenHandler(enrollment.mentor?._id)
                    : undefined
                }
              >
                {enrollment.mentor?.name ?? 'n/a'}
              </Table.Cell>
              <Table.Cell>
                {enrollment.startDate ? moment(enrollment.startDate).format('DD.MM.YYYY') : 'n/a'}
              </Table.Cell>
              <Table.Cell>
                {enrollment.endDate ? moment(enrollment.endDate).format('DD.MM.YYYY') : 'n/a'}
              </Table.Cell>
              <Table.Cell>{ENROLLMENT_STATUS_TYPES[enrollment.status] ?? 'n/a'}</Table.Cell>
              <Table.Cell>
                {enrollment.lastSessionDate
                  ? moment(enrollment.lastSessionDate).format('DD.MM.YYYY')
                  : 'n/a'}
              </Table.Cell>
              <Table.Cell>
                <Tooltip
                  placement="top"
                  text="Completed Sessions / Scheduled or Ongoing Sessions / Total No. of Sessions"
                >
                  <div className="flex flex-col gap-1">
                    <p>
                      {`${enrollment.noOfSessions.completed || 0} / ${
                        enrollment.noOfSessions.scheduledOrPending || 0
                      } / ${enrollment.noOfSessions.total || 0}`}
                    </p>
                    <div className="relative w-full h-2 rounded-full bg-customGrey bg-opacity-50 min-w-24">
                      <div
                        style={{
                          width: getCourseProgressPercentage(
                            enrollment.noOfSessions.completed,
                            enrollment?.noOfSessions.total,
                            enrollment.noOfSessions.scheduledOrPending || 0
                          ),
                          background: `${enrollment?.course?.color ?? COLORS.PRIMARY}59`,
                        }}
                        className={mergeClassNames(`rounded-full h-full`)}
                      />
                      <div
                        style={{
                          width: getCourseProgressPercentage(
                            enrollment.noOfSessions.completed,
                            enrollment?.noOfSessions.total
                          ),
                          background: enrollment?.course?.color ?? COLORS.PRIMARY,
                        }}
                        className={mergeClassNames(`absolute top-0 left-0 rounded-full h-full`)}
                      />
                    </div>
                  </div>
                </Tooltip>
              </Table.Cell>
              <Table.Cell>
                <div className="flex gap-2">
                  <span
                    className="cursor-pointer underline"
                    onClick={() => enrollmentModalOpenHandler(enrollment?._id ?? '')}
                  >
                    View
                  </span>
                </div>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </section>
  );
};

export default EnrollmentsTable;
